/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ApproveComponentDraftRequestDtoPartial } from '../models/approve-component-draft-request-dto-partial';
import { ComponentDefinition } from '../models/component-definition';
import { ComponentDefinitionFileRequestDto } from '../models/component-definition-file-request-dto';
import { ComponentDefinitionUpdateDto } from '../models/component-definition-update-dto';
import { ComponentDefinitionWithRelations } from '../models/component-definition-with-relations';
import { DataSchemaField } from '../models/data-schema-field';
import { NewComponentDefinition } from '../models/new-component-definition';
import { OpenAiRequestDto } from '../models/open-ai-request-dto';
import { Count as LoopbackCount } from '../models/loopback/count';

@Injectable({
  providedIn: 'root',
})
export class ComponentDefinitionControllerService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation componentDefinitionControllerCount
   */
  static readonly ComponentDefinitionControllerCountPath = '/component-definitions/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `count()` instead.
   *
   * This method doesn't expect any request body.
   */
  count$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<LoopbackCount>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LoopbackCount>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `count$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  count(params?: {
    where?: any;
  }): Observable<LoopbackCount> {

    return this.count$Response(params).pipe(
      map((r: StrictHttpResponse<LoopbackCount>) => r.body as LoopbackCount)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerApproveDraft
   */
  static readonly ComponentDefinitionControllerApproveDraftPath = '/component-definitions/drafts/{draftId}/approve';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `approveDraft()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  approveDraft$Response(params: {
    draftId: string;
    body?: ApproveComponentDraftRequestDtoPartial
  }): Observable<StrictHttpResponse<ComponentDefinition>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerApproveDraftPath, 'post');
    if (params) {
      rb.path('draftId', params.draftId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ComponentDefinition>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `approveDraft$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  approveDraft(params: {
    draftId: string;
    body?: ApproveComponentDraftRequestDtoPartial
  }): Observable<ComponentDefinition> {

    return this.approveDraft$Response(params).pipe(
      map((r: StrictHttpResponse<ComponentDefinition>) => r.body as ComponentDefinition)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerRejectDraft
   */
  static readonly ComponentDefinitionControllerRejectDraftPath = '/component-definitions/drafts/{draftId}/reject';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `rejectDraft()` instead.
   *
   * This method doesn't expect any request body.
   */
  rejectDraft$Response(params: {
    draftId: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerRejectDraftPath, 'post');
    if (params) {
      rb.path('draftId', params.draftId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `rejectDraft$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  rejectDraft(params: {
    draftId: string;
  }): Observable<void> {

    return this.rejectDraft$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerRevertToStep
   */
  static readonly ComponentDefinitionControllerRevertToStepPath = '/component-definitions/drafts/{draftId}/revert-to-step/{stepId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `revertToStep()` instead.
   *
   * This method doesn't expect any request body.
   */
  revertToStep$Response(params: {
    draftId: string;
    stepId: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerRevertToStepPath, 'post');
    if (params) {
      rb.path('draftId', params.draftId, {});
      rb.path('stepId', params.stepId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `revertToStep$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  revertToStep(params: {
    draftId: string;
    stepId: string;
  }): Observable<void> {

    return this.revertToStep$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerGetDraftSteps
   */
  static readonly ComponentDefinitionControllerGetDraftStepsPath = '/component-definitions/drafts/{draftId}/steps';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDraftSteps()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDraftSteps$Response(params: {
    draftId: string;
  }): Observable<StrictHttpResponse<Array<any>>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerGetDraftStepsPath, 'get');
    if (params) {
      rb.path('draftId', params.draftId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<any>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getDraftSteps$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDraftSteps(params: {
    draftId: string;
  }): Observable<Array<any>> {

    return this.getDraftSteps$Response(params).pipe(
      map((r: StrictHttpResponse<Array<any>>) => r.body as Array<any>)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerGetDraftById
   */
  static readonly ComponentDefinitionControllerGetDraftByIdPath = '/component-definitions/drafts/{draftId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDraftById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDraftById$Response(params: {
    draftId: string;
  }): Observable<StrictHttpResponse<{  }>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerGetDraftByIdPath, 'get');
    if (params) {
      rb.path('draftId', params.draftId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{  }>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getDraftById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDraftById(params: {
    draftId: string;
  }): Observable<{  }> {

    return this.getDraftById$Response(params).pipe(
      map((r: StrictHttpResponse<{  }>) => r.body as {  })
    );
  }

  /**
   * Path part for operation componentDefinitionControllerUpdateComponentDefinitionFileContent
   */
  static readonly ComponentDefinitionControllerUpdateComponentDefinitionFileContentPath = '/component-definitions/file-content';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateComponentDefinitionFileContent()` instead.
   *
   * This method sends `text/plain` and handles request body of type `text/plain`.
   */
  updateComponentDefinitionFileContent$Response(params?: {
    filePath?: string;
    commitMessage?: string;
    body?: string
  }): Observable<StrictHttpResponse<string>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerUpdateComponentDefinitionFileContentPath, 'patch');
    if (params) {
      rb.query('filePath', params.filePath, {});
      rb.query('commitMessage', params.commitMessage, {});
      rb.body(params.body, 'text/plain');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<string>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateComponentDefinitionFileContent$Response()` instead.
   *
   * This method sends `text/plain` and handles request body of type `text/plain`.
   */
  updateComponentDefinitionFileContent(params?: {
    filePath?: string;
    commitMessage?: string;
    body?: string
  }): Observable<string> {

    return this.updateComponentDefinitionFileContent$Response(params).pipe(
      map((r: StrictHttpResponse<string>) => r.body as string)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerGenerateByAi
   */
  static readonly ComponentDefinitionControllerGenerateByAiPath = '/component-definitions/generate-by-ai';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `generateByAI()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  generateByAI$Response(params?: {
    body?: OpenAiRequestDto
  }): Observable<StrictHttpResponse<{ 'draftId'?: string, 'componentId'?: string, 'stepId'?: string, 'summary'?: string }>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerGenerateByAiPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{ 'draftId'?: string, 'componentId'?: string, 'stepId'?: string, 'summary'?: string }>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `generateByAI$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  generateByAI(params?: {
    body?: OpenAiRequestDto
  }): Observable<{ 'draftId'?: string, 'componentId'?: string, 'stepId'?: string, 'summary'?: string }> {

    return this.generateByAI$Response(params).pipe(
      map((r: StrictHttpResponse<{ 'draftId'?: string, 'componentId'?: string, 'stepId'?: string, 'summary'?: string }>) => r.body as { 'draftId'?: string, 'componentId'?: string, 'stepId'?: string, 'summary'?: string })
    );
  }

  /**
   * Path part for operation componentDefinitionControllerGenerateByAiAssistant
   */
  static readonly ComponentDefinitionControllerGenerateByAiAssistantPath = '/component-definitions/generate-by-ai-assistant';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `generateByAIAssistant()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  generateByAIAssistant$Response(params: {

    /**
     * Request body for multipart/form-data based file upload
     */
    body: { 'file'?: Blob }
  }): Observable<StrictHttpResponse<{ 'draftId'?: string, 'componentId'?: string, 'stepId'?: string, 'summary'?: string }>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerGenerateByAiAssistantPath, 'post');
    if (params) {
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{ 'draftId'?: string, 'componentId'?: string, 'stepId'?: string, 'summary'?: string }>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `generateByAIAssistant$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  generateByAIAssistant(params: {

    /**
     * Request body for multipart/form-data based file upload
     */
    body: { 'file'?: Blob }
  }): Observable<{ 'draftId'?: string, 'componentId'?: string, 'stepId'?: string, 'summary'?: string }> {

    return this.generateByAIAssistant$Response(params).pipe(
      map((r: StrictHttpResponse<{ 'draftId'?: string, 'componentId'?: string, 'stepId'?: string, 'summary'?: string }>) => r.body as { 'draftId'?: string, 'componentId'?: string, 'stepId'?: string, 'summary'?: string })
    );
  }

  /**
   * Path part for operation componentDefinitionControllerGetAllWithDetails
   */
  static readonly ComponentDefinitionControllerGetAllWithDetailsPath = '/component-definitions/get-all-with-details';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllWithDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllWithDetails$Response(params?: {
  }): Observable<StrictHttpResponse<ComponentDefinitionWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerGetAllWithDetailsPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ComponentDefinitionWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getAllWithDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllWithDetails(params?: {
  }): Observable<ComponentDefinitionWithRelations> {

    return this.getAllWithDetails$Response(params).pipe(
      map((r: StrictHttpResponse<ComponentDefinitionWithRelations>) => r.body as ComponentDefinitionWithRelations)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerGetBrokenFieldsFromComponentDefinitionId
   */
  static readonly ComponentDefinitionControllerGetBrokenFieldsFromComponentDefinitionIdPath = '/component-definitions/get-broken-fields/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getBrokenFieldsFromComponentDefinitionId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getBrokenFieldsFromComponentDefinitionId$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<Array<DataSchemaField>>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerGetBrokenFieldsFromComponentDefinitionIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<DataSchemaField>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getBrokenFieldsFromComponentDefinitionId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getBrokenFieldsFromComponentDefinitionId(params: {
    id: string;
  }): Observable<Array<DataSchemaField>> {

    return this.getBrokenFieldsFromComponentDefinitionId$Response(params).pipe(
      map((r: StrictHttpResponse<Array<DataSchemaField>>) => r.body as Array<DataSchemaField>)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerGetByIdWithDetails
   */
  static readonly ComponentDefinitionControllerGetByIdWithDetailsPath = '/component-definitions/get-by-id-with-details/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getByIdWithDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  getByIdWithDetails$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<ComponentDefinitionWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerGetByIdWithDetailsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ComponentDefinitionWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getByIdWithDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getByIdWithDetails(params: {
    id: string;
  }): Observable<ComponentDefinitionWithRelations> {

    return this.getByIdWithDetails$Response(params).pipe(
      map((r: StrictHttpResponse<ComponentDefinitionWithRelations>) => r.body as ComponentDefinitionWithRelations)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerChangeActiveVersion
   */
  static readonly ComponentDefinitionControllerChangeActiveVersionPath = '/component-definitions/{componentId}/change-active-version';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `changeActiveVersion()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  changeActiveVersion$Response(params: {
    componentId: string;
    body?: { 'targetVersion': string }
  }): Observable<StrictHttpResponse<{  }>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerChangeActiveVersionPath, 'post');
    if (params) {
      rb.path('componentId', params.componentId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{  }>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `changeActiveVersion$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  changeActiveVersion(params: {
    componentId: string;
    body?: { 'targetVersion': string }
  }): Observable<{  }> {

    return this.changeActiveVersion$Response(params).pipe(
      map((r: StrictHttpResponse<{  }>) => r.body as {  })
    );
  }

  /**
   * Path part for operation componentDefinitionControllerDeprecateById
   */
  static readonly ComponentDefinitionControllerDeprecateByIdPath = '/component-definitions/{id}/deprecate';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deprecateById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deprecateById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerDeprecateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deprecateById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deprecateById(params: {
    id: string;
  }): Observable<void> {

    return this.deprecateById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerGetDraftBundle
   */
  static readonly ComponentDefinitionControllerGetDraftBundlePath = '/component-definitions/{componentId}/draft-bundle';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDraftBundle()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDraftBundle$Response(params: {
    componentId: string;
    stepId?: string;
  }): Observable<StrictHttpResponse<string>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerGetDraftBundlePath, 'get');
    if (params) {
      rb.path('componentId', params.componentId, {});
      rb.query('stepId', params.stepId, {});
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: 'application/javascript'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<string>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getDraftBundle$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDraftBundle(params: {
    componentId: string;
    stepId?: string;
  }): Observable<string> {

    return this.getDraftBundle$Response(params).pipe(
      map((r: StrictHttpResponse<string>) => r.body as string)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerGetDraftsByComponentId
   */
  static readonly ComponentDefinitionControllerGetDraftsByComponentIdPath = '/component-definitions/{componentId}/drafts';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDraftsByComponentId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDraftsByComponentId$Response(params: {
    componentId: string;
  }): Observable<StrictHttpResponse<Array<any>>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerGetDraftsByComponentIdPath, 'get');
    if (params) {
      rb.path('componentId', params.componentId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<any>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getDraftsByComponentId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDraftsByComponentId(params: {
    componentId: string;
  }): Observable<Array<any>> {

    return this.getDraftsByComponentId$Response(params).pipe(
      map((r: StrictHttpResponse<Array<any>>) => r.body as Array<any>)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerGetMainBundle
   */
  static readonly ComponentDefinitionControllerGetMainBundlePath = '/component-definitions/{componentId}/main-bundle';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMainBundle()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMainBundle$Response(params: {
    componentId: string;
    version?: string;
  }): Observable<StrictHttpResponse<string>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerGetMainBundlePath, 'get');
    if (params) {
      rb.path('componentId', params.componentId, {});
      rb.query('version', params.version, {});
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: 'application/javascript'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<string>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getMainBundle$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMainBundle(params: {
    componentId: string;
    version?: string;
  }): Observable<string> {

    return this.getMainBundle$Response(params).pipe(
      map((r: StrictHttpResponse<string>) => r.body as string)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerUpdateByAi
   */
  static readonly ComponentDefinitionControllerUpdateByAiPath = '/component-definitions/{id}/update-by-ai';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateByAI()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  updateByAI$Response(params: {
    id: string;

    /**
     * Request body for multipart/form-data based file upload
     */
    body: { 'file'?: Blob }
  }): Observable<StrictHttpResponse<{ 'draftId'?: string, 'stepId'?: string, 'summary'?: string }>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerUpdateByAiPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{ 'draftId'?: string, 'stepId'?: string, 'summary'?: string }>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateByAI$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  updateByAI(params: {
    id: string;

    /**
     * Request body for multipart/form-data based file upload
     */
    body: { 'file'?: Blob }
  }): Observable<{ 'draftId'?: string, 'stepId'?: string, 'summary'?: string }> {

    return this.updateByAI$Response(params).pipe(
      map((r: StrictHttpResponse<{ 'draftId'?: string, 'stepId'?: string, 'summary'?: string }>) => r.body as { 'draftId'?: string, 'stepId'?: string, 'summary'?: string })
    );
  }

  /**
   * Path part for operation componentDefinitionControllerUpdateFiles
   */
  static readonly ComponentDefinitionControllerUpdateFilesPath = '/component-definitions/{id}/update-files';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateFiles()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFiles$Response(params: {
    id: string;
    body?: ComponentDefinitionFileRequestDto
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerUpdateFilesPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateFiles$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFiles(params: {
    id: string;
    body?: ComponentDefinitionFileRequestDto
  }): Observable<void> {

    return this.updateFiles$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerFindById
   */
  static readonly ComponentDefinitionControllerFindByIdPath = '/component-definitions/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<ComponentDefinition>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ComponentDefinition>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById(params: {
    id: string;
    filter?: any;
  }): Observable<ComponentDefinition> {

    return this.findById$Response(params).pipe(
      map((r: StrictHttpResponse<ComponentDefinition>) => r.body as ComponentDefinition)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerDeleteById
   */
  static readonly ComponentDefinitionControllerDeleteByIdPath = '/component-definitions/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById(params: {
    id: string;
  }): Observable<void> {

    return this.deleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerUpdateById
   */
  static readonly ComponentDefinitionControllerUpdateByIdPath = '/component-definitions/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById$Response(params: {
    id: string;
    body?: ComponentDefinitionUpdateDto
  }): Observable<StrictHttpResponse<ComponentDefinition>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ComponentDefinition>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById(params: {
    id: string;
    body?: ComponentDefinitionUpdateDto
  }): Observable<ComponentDefinition> {

    return this.updateById$Response(params).pipe(
      map((r: StrictHttpResponse<ComponentDefinition>) => r.body as ComponentDefinition)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerFind
   */
  static readonly ComponentDefinitionControllerFindPath = '/component-definitions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `find()` instead.
   *
   * This method doesn't expect any request body.
   */
  find$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<ComponentDefinition>>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ComponentDefinition>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `find$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  find(params?: {
    filter?: any;
  }): Observable<Array<ComponentDefinition>> {

    return this.find$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ComponentDefinition>>) => r.body as Array<ComponentDefinition>)
    );
  }

  /**
   * Path part for operation componentDefinitionControllerCreate
   */
  static readonly ComponentDefinitionControllerCreatePath = '/component-definitions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `create()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create$Response(params?: {
    body?: NewComponentDefinition
  }): Observable<StrictHttpResponse<ComponentDefinition>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentDefinitionControllerService.ComponentDefinitionControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ComponentDefinition>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `create$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create(params?: {
    body?: NewComponentDefinition
  }): Observable<ComponentDefinition> {

    return this.create$Response(params).pipe(
      map((r: StrictHttpResponse<ComponentDefinition>) => r.body as ComponentDefinition)
    );
  }

}
